import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { notification } from 'antd';
import { SearchOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import {
  TitleRow,
  ContainerRow,
  SearchInputStyle,
  ControlsDiV,
  AddButton
} from '../styledComponents';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import CustomSideDrawer from '../../CustomComponents/CustomSideDrawer';
import GenericNTAForm from './GenericNTAForm';
import NTAColumnSettingsModal from './NTAColumnSettingsModal';
import { getColumnPreferences, saveColumnPreferences } from '../../../Apis/Clinic';

const NTATab = ({
  createColumns,
  storageKey,
  tabLabel,
  formFields,
  apiHandlers,
  pageName
}) => {
  const hasApi = Boolean(apiHandlers?.fetchAll);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});

  // Derive column list for the modal from the column definitions
  const columnDefs = useMemo(
    () => createColumns(() => {}, () => {}, () => {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const MANDATORY_COLUMN_IDS = new Set([
    'actions',
    'groupCode',
    'comorbidityCode',
    'icd10Undotted'
  ]);

  const modalColumnConfig = useMemo(
    () =>
      columnDefs.map(col => ({
        key: col.id,
        label: typeof col.header === 'string' ? col.header : col.id,
        mandatory: MANDATORY_COLUMN_IDS.has(col.id)
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnDefs]
  );

  const defaultVisibleColumns = useMemo(
    () => columnDefs.reduce((acc, col) => { acc[col.id] = true; return acc; }, {}),
    [columnDefs]
  );

  // Fetch column preferences from API
  const fetchColumnPreferences = useCallback(async () => {
    if (!pageName) return;
    try {
      const response = await getColumnPreferences(pageName);
      if (
        response.data?.result?.length > 0
      ) {
        const prefs = response.data.result.reduce((acc, item) => {
          acc[item.columnName] = item.selected;
          return acc;
        }, {});
        const mandatoryOverrides = {};
        MANDATORY_COLUMN_IDS.forEach(id => { mandatoryOverrides[id] = true; });
        setVisibleColumns({
          ...defaultVisibleColumns,
          ...prefs,
          ...mandatoryOverrides
        });
      } else {
        setVisibleColumns(defaultVisibleColumns);
      }
    } catch {
      setVisibleColumns(defaultVisibleColumns);
    }
  }, [pageName, defaultVisibleColumns]);

  useEffect(() => {
    fetchColumnPreferences();
  }, [fetchColumnPreferences]);

  // Data fetch
  const fetchAll = () => {
    setLoading(true);
    apiHandlers
      .fetchAll()
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          setData(response.data.result || []);
        } else {
          notification.error({ message: 'Failed', description: response.data.message });
        }
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (hasApi) fetchAll();
    // eslint-disable-next-line
  }, []);

  const handleSubmit = values => {
    if (!hasApi) {
      if (selectedItem) {
        setData(prev =>
          prev.map(item =>
            item.id === selectedItem.id ? { ...selectedItem, ...values } : item
          )
        );
      } else {
        setData(prev => [...prev, { ...values, id: Date.now() }]);
      }
      setDrawerVisible(false);
      setSelectedItem(null);
      return;
    }

    setSubmitLoading(true);
    const apiCall = selectedItem
      ? apiHandlers.update(selectedItem.id, { ...values, id: selectedItem.id })
      : apiHandlers.create(values);

    apiCall
      .then(response => {
        setSubmitLoading(false);
        if (response.data.success) {
          notification.success({
            message: selectedItem ? 'Updated' : 'Added',
            description: `${tabLabel} ${selectedItem ? 'updated' : 'added'} successfully.`
          });
          setDrawerVisible(false);
          setSelectedItem(null);
          fetchAll();
        } else {
          notification.error({ message: 'Failed', description: response.data.message });
        }
      })
      .catch(() => setSubmitLoading(false));
  };

  const handleDelete = id => {
    if (apiHandlers?.delete) {
      apiHandlers
        .delete(id)
        .then(response => {
          if (response.data.success) {
            notification.success({
              message: 'Deleted',
              description: `${tabLabel} deleted successfully.`
            });
            fetchAll();
          } else {
            notification.error({ message: 'Failed', description: response.data.message });
          }
        })
        .catch(() => {});
    } else {
      setData(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleSaveColumnSettings = newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);
    const columnPreferencesArray = Object.keys(newVisibleColumns).map(columnName => ({
      columnName,
      selected: newVisibleColumns[columnName]
    }));
    saveColumnPreferences(pageName, columnPreferencesArray)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully.'
        });
        fetchColumnPreferences();
      })
      .catch(() => {
        notification.error({
          message: 'Error',
          description: 'Failed to save column preferences. Please try again.'
        });
      });
  };

  const columns = createColumns(setSelectedItem, setDrawerVisible, handleDelete);

  return (
    <>
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <div />
          <ControlsDiV>
            <AddButton
              icon={<PlusOutlined />}
              type='primary'
              onClick={() => {
                setSelectedItem(null);
                setDrawerVisible(true);
              }}
            >
              Add
            </AddButton>
            <AddButton
              icon={<SettingOutlined />}
              onClick={() => setColumnSettingsVisible(true)}
            >
              Columns
            </AddButton>
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              value={searchString}
              onChange={e => setSearchString(e.target.value)}
            />
            <AddButton
              onClick={() => {
                setSearchString('');
                setClearFiltersTrigger(t => t + 1);
              }}
            >
              Clear Filter
            </AddButton>
          </ControlsDiV>
        </TitleRow>

        <TanStackUserTable
          data={data}
          columns={columns}
          loading={loading}
          searchValue={searchString}
          storageKey={storageKey}
          clearFiltersTrigger={clearFiltersTrigger}
          columnVisibility={Object.keys(visibleColumns).length > 0 ? visibleColumns : undefined}
        />

        <NTAColumnSettingsModal
          visible={columnSettingsVisible}
          onCancel={() => setColumnSettingsVisible(false)}
          onSave={handleSaveColumnSettings}
          visibleColumns={
            Object.keys(visibleColumns).length > 0 ? visibleColumns : defaultVisibleColumns
          }
          columnConfig={modalColumnConfig}
        />

        <CustomSideDrawer
          title={selectedItem ? `Edit ${tabLabel}` : `Add ${tabLabel}`}
          placement='right'
          closable={false}
          onClose={() => {
            setSelectedItem(null);
            setDrawerVisible(false);
          }}
          visible={drawerVisible}
          width={420}
          destroyOnClose={true}
        >
          <GenericNTAForm
            fields={formFields}
            selectedItem={selectedItem}
            onSubmit={handleSubmit}
            submitLoading={submitLoading}
            onCancel={() => {
              setSelectedItem(null);
              setDrawerVisible(false);
            }}
          />
        </CustomSideDrawer>
      </ContainerRow>
    </>
  );
};

export default NTATab;
